import { InjectionToken } from '@angular/core';

/**
 * Shape of the brand configuration object injected at app-bootstrap time.
 * Each brand app provides its own `BrandConfig` via `APP_BRAND_CONFIG`.
 */
export type BrandConfig = {
  /** Unique identifier for the brand (used e.g. as localStorage key prefix). */
  brandId: string;
  /** Human-readable application name shown in titles and headers. */
  appName: string;
  /** Absolute or relative URL to the brand logo image. When omitted the app name is rendered as text instead. */
  logoUrl?: string;
  /** Absolute or relative URL to the logo image used in dark mode. Falls back to logoUrl when omitted. */
  logoDarkUrl?: string;
  /** Absolute or relative URL to the favicon. */
  faviconUrl: string;
  /** BCP-47 language codes supported by this brand (e.g. `['en', 'ar']`). */
  supportedLangs: string[];
  /** Default language code to activate on first visit. */
  defaultLang: string;
  /** Map of feature-flag keys to their enabled state for this brand. */
  featureFlags: Record<string, boolean>;
  /** Optional override for the API base URL (falls back to EnvironmentService). */
  apiBaseUrl?: string;
  /** Authentication mechanism used by this brand. */
  authStrategy: 'email-password' | 'magic-link' | 'oidc';
};

/**
 * Injection token for the active brand configuration.
 * Provide this token in each brand's `app.config.ts` via the brand's
 * `provideXxxBrandConfig()` helper.
 *
 * @example
 * // In a component or guard:
 * private readonly brandConfig = inject(APP_BRAND_CONFIG);
 */
export const APP_BRAND_CONFIG = new InjectionToken<BrandConfig>(
  'APP_BRAND_CONFIG'
);
